/**
 * Edit Asset workflow.
 * 1. Search/scan to find asset
 * 2. Edit fields (name, serial, model, status, notes)
 * 3. Queue update operation
 */

import { router } from '../router.js';
import { enqueue } from '../db.js';
import { syncDirect } from '../sync.js';
import { searchAssets, getAsset, getAssetByTag, getModels, getStatusLabels } from '../snipeit.js';
import { MAC_COLUMN_WLAN, MAC_COLUMN_LAN, MAC_COLUMN_IP } from '../settings.js';
import { showToast } from '../toast.js';
import { hapticSuccess, hapticTap } from '../haptics.js';
import { startScanner, stopScanner } from './scanner.js';

export function renderEditAsset() {
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="page">
      <!-- Screen-reader live region for search result announcements -->
      <div id="ea-sr-status" aria-live="polite" aria-atomic="true" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;"></div>

      <div class="page-header">
        <button class="back-btn" id="back-btn" aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1>Edit Asset</h1>
      </div>

      <!-- Search section -->
      <div id="ea-search-section">
        <div class="card">
          <h2 style="margin-bottom:12px;">Find Asset</h2>

          <div style="display:flex; gap:8px; margin-bottom:12px;">
            <button class="btn btn-secondary btn-sm" id="ea-scan-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="12" x2="17" y2="12"/></svg>
              Scan
            </button>
          </div>

          <div id="ea-scanner-area" class="hidden">
            <div class="camera-container" id="ea-scanner-reader"></div>
            <button class="btn btn-secondary btn-block btn-sm" id="ea-scanner-close">Close Scanner</button>
          </div>

          <div class="search-wrap">
            <span class="search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </span>
            <input type="text" id="ea-asset-search" class="form-control" placeholder="Search by name, tag, or serial..." style="padding-left:38px;" aria-autocomplete="list" aria-controls="ea-asset-results" />
          </div>

          <div id="ea-asset-results" class="search-results hidden" role="listbox"></div>
        </div>
      </div>

      <!-- Edit form (hidden until asset selected) -->
      <div id="ea-edit-section" class="hidden">
        <div class="card" style="margin-bottom:12px; background: var(--color-primary-light); border-color: var(--color-primary);">
          <div style="display:flex; justify-content:space-between; align-items:center;">
            <div>
              <strong id="ea-current-name"></strong>
              <div style="font-size:0.8rem; color:var(--color-text-muted);" id="ea-current-tag"></div>
            </div>
            <button class="btn btn-secondary btn-sm" id="ea-change-asset">Change</button>
          </div>
        </div>

        <form id="ea-form">
          <div class="form-group">
            <label for="ea-name">Asset Name</label>
            <input type="text" id="ea-name" class="form-control" required />
          </div>

          <div class="form-group">
            <label for="ea-serial">Serial Number</label>
            <input type="text" id="ea-serial" class="form-control" />
          </div>

          <div class="form-group">
            <label for="ea-model">Model</label>
            <select id="ea-model" class="form-control">
              <option value="">Select model...</option>
            </select>
          </div>

          <div class="form-group">
            <label for="ea-status">Status</label>
            <select id="ea-status" class="form-control">
              <option value="">Select status...</option>
            </select>
          </div>

          <div class="form-group">
            <label for="ea-wlan-mac">WLAN MAC Address</label>
            <input type="text" id="ea-wlan-mac" class="form-control" placeholder="AA:BB:CC:DD:EE:FF" autocomplete="off" />
          </div>

          <div class="form-group">
            <label for="ea-lan-mac">LAN MAC Address</label>
            <input type="text" id="ea-lan-mac" class="form-control" placeholder="AA:BB:CC:DD:EE:FF" autocomplete="off" />
          </div>

          <div class="form-group">
            <label for="ea-ip">Local IP Address</label>
            <input type="text" id="ea-ip" class="form-control" placeholder="192.168.1.100" autocomplete="off" />
          </div>

          <div class="form-group">
            <label for="ea-notes">Notes</label>
            <textarea id="ea-notes" class="form-control" rows="2"></textarea>
          </div>

          <div style="display:flex; gap:8px; margin-top:16px;">
            <button type="button" class="btn btn-secondary" style="flex:1" id="ea-queue">Add to Queue</button>
            <button type="button" class="btn btn-primary" style="flex:1" id="ea-sync">Sync Now</button>
          </div>
        </form>
      </div>
    </div>
  `;

  let selectedAssetId = null;
  let searchTimeout = null;

  // ── Load dropdowns ──
  loadModels();
  loadStatuses();

  // ── Back ──
  document.getElementById('back-btn').addEventListener('click', () => {
    router.navigate('/');
  });

  // ── Scan ──
  document.getElementById('ea-scan-btn').addEventListener('click', () => {
    hapticTap();
    const area = document.getElementById('ea-scanner-area');
    area.classList.remove('hidden');
    startScanner('ea-scanner-reader', async (text) => {
      hapticSuccess();
      stopScanner();
      area.classList.add('hidden');
      document.getElementById('ea-asset-search').value = text;
      try {
        const asset = await getAssetByTag(text);
        selectAsset(asset);
      } catch {
        showToast('Asset not found for tag: ' + text);
      }
    });
  });

  document.getElementById('ea-scanner-close').addEventListener('click', () => {
    stopScanner();
    document.getElementById('ea-scanner-area').classList.add('hidden');
  });

  // ── Asset search ──
  document.getElementById('ea-asset-search').addEventListener('input', (e) => {
    clearTimeout(searchTimeout);
    const query = e.target.value.trim();
    if (query.length < 2) {
      document.getElementById('ea-asset-results').classList.add('hidden');
      return;
    }
    searchTimeout = setTimeout(async () => {
      try {
        const res = await searchAssets(query);
        const container = document.getElementById('ea-asset-results');
        const rows = res.rows || [];
        container.innerHTML = '';
        if (rows.length === 0) {
          container.innerHTML = `<div class="result-empty">No results for "${escapeHtml(query)}"</div>`;
          container.classList.remove('hidden');
          document.getElementById('ea-sr-status').textContent = 'No results found';
        } else {
          rows.forEach((a) => {
            const div = document.createElement('div');
            div.className = 'result-item';
            div.setAttribute('role', 'option');
            div.setAttribute('tabindex', '0');
            div.innerHTML = `
              <div>${escapeHtml(a.name || 'Unnamed')}</div>
              <div class="result-sub">Tag: ${escapeHtml(a.asset_tag || '')} | Serial: ${escapeHtml(a.serial || 'N/A')}</div>
            `;
            div.addEventListener('click', () => selectAsset(a));
            div.addEventListener('keydown', (ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); selectAsset(a); }
            });
            container.appendChild(div);
          });
          container.classList.remove('hidden');
          document.getElementById('ea-sr-status').textContent = `${rows.length} result${rows.length !== 1 ? 's' : ''} found`;
        }
      } catch {
        showToast('Search failed');
      }
    }, 300);
  });

  async function selectAsset(asset) {
    // If we only have partial data, fetch full asset
    let full = asset;
    if (!asset._full) {
      try {
        full = await getAsset(asset.id);
      } catch {
        full = asset;
      }
    }

    selectedAssetId = full.id;

    document.getElementById('ea-current-name').textContent = full.name || 'Unnamed';
    document.getElementById('ea-current-tag').textContent = `Tag: ${full.asset_tag || 'N/A'}`;

    // Fill form fields
    document.getElementById('ea-name').value = full.name || '';
    document.getElementById('ea-serial').value = full.serial || '';
    document.getElementById('ea-wlan-mac').value = full.custom_fields?.[MAC_COLUMN_WLAN]?.value || '';
    document.getElementById('ea-lan-mac').value = full.custom_fields?.[MAC_COLUMN_LAN]?.value || '';
    document.getElementById('ea-ip').value = full.custom_fields?.[MAC_COLUMN_IP]?.value || '';
    document.getElementById('ea-notes').value = full.notes || '';

    // Select model if present
    if (full.model?.id) {
      const modelSelect = document.getElementById('ea-model');
      if (modelSelect.querySelector(`option[value="${full.model.id}"]`)) {
        modelSelect.value = full.model.id;
      }
    }

    // Select status if present
    if (full.status_label?.id) {
      const statusSelect = document.getElementById('ea-status');
      if (statusSelect.querySelector(`option[value="${full.status_label.id}"]`)) {
        statusSelect.value = full.status_label.id;
      }
    }

    document.getElementById('ea-search-section').classList.add('hidden');
    document.getElementById('ea-edit-section').classList.remove('hidden');
    document.getElementById('ea-asset-results').classList.add('hidden');
  }

  // ── Change asset ──
  document.getElementById('ea-change-asset').addEventListener('click', () => {
    selectedAssetId = null;
    document.getElementById('ea-search-section').classList.remove('hidden');
    document.getElementById('ea-edit-section').classList.add('hidden');
  });

  // ── Form submit ──
  function buildEditPayload() {
    const modelId = document.getElementById('ea-model').value;
    const statusId = document.getElementById('ea-status').value;
    const wlanMac = document.getElementById('ea-wlan-mac').value.trim();
    const lanMac = document.getElementById('ea-lan-mac').value.trim();
    const ip = document.getElementById('ea-ip').value.trim();
    const payload = {
      asset_id: selectedAssetId,
      name: document.getElementById('ea-name').value.trim(),
      serial: document.getElementById('ea-serial').value.trim(),
      notes: document.getElementById('ea-notes').value.trim(),
    };
    if (modelId) payload.model_id = parseInt(modelId, 10);
    if (statusId) payload.status_id = parseInt(statusId, 10);
    if (wlanMac) payload[MAC_COLUMN_WLAN] = wlanMac;
    if (lanMac) payload[MAC_COLUMN_LAN] = lanMac;
    if (ip) payload[MAC_COLUMN_IP] = ip;
    return payload;
  }

  document.getElementById('ea-queue').addEventListener('click', async () => {
    const nameEl = document.getElementById('ea-name');
    if (!selectedAssetId || !nameEl.value.trim()) {
      showToast('Asset name is required');
      nameEl.setAttribute('aria-invalid', 'true');
      nameEl.focus();
      return;
    }
    nameEl.removeAttribute('aria-invalid');
    await enqueue('update_asset', buildEditPayload());
    hapticSuccess();
    showToast('Update added to queue');
    window.dispatchEvent(new CustomEvent('queue-updated'));
    router.navigate('/');
  });

  document.getElementById('ea-sync').addEventListener('click', async () => {
    const nameEl = document.getElementById('ea-name');
    if (!selectedAssetId || !nameEl.value.trim()) {
      showToast('Asset name is required');
      nameEl.setAttribute('aria-invalid', 'true');
      nameEl.focus();
      return;
    }
    nameEl.removeAttribute('aria-invalid');
    const btn = document.getElementById('ea-sync');
    btn.disabled = true;
    btn.textContent = 'Syncing...';
    try {
      await syncDirect('update_asset', buildEditPayload());
      hapticSuccess();
      showToast('Asset updated');
      router.navigate('/');
    } catch (err) {
      showToast('Sync failed: ' + err.message);
      btn.disabled = false;
      btn.textContent = 'Sync Now';
    }
  });

  return () => {
    stopScanner();
  };
}

async function loadModels() {
  const select = document.getElementById('ea-model');
  if (!select) return;
  const loadingOpt = document.createElement('option');
  loadingOpt.disabled = true;
  loadingOpt.textContent = 'Loading...';
  select.insertBefore(loadingOpt, select.firstChild);
  try {
    const res = await getModels();
    loadingOpt.remove();
    (res.rows || []).forEach((m) => {
      const opt = document.createElement('option');
      opt.value = m.id;
      opt.textContent = m.name;
      select.appendChild(opt);
    });
  } catch {
    loadingOpt.textContent = 'Failed to load';
  }
}

async function loadStatuses() {
  const select = document.getElementById('ea-status');
  if (!select) return;
  const loadingOpt = document.createElement('option');
  loadingOpt.disabled = true;
  loadingOpt.textContent = 'Loading...';
  select.insertBefore(loadingOpt, select.firstChild);
  try {
    const res = await getStatusLabels();
    loadingOpt.remove();
    (res.rows || []).forEach((s) => {
      const opt = document.createElement('option');
      opt.value = s.id;
      opt.textContent = s.name;
      select.appendChild(opt);
    });
  } catch {
    loadingOpt.textContent = 'Failed to load';
  }
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
